import { PointerEventHandler, ReactNode } from 'react';

export type TCalculatorMode = 'basic' | 'scientific' | 'converter';

export type TCalculatorAction =
  | 'handleNumber'
  | 'handleOperator'
  | 'handleClear'
  | 'handleEquals'
  | 'handleDecimal'
  | 'handlePercent'
  | 'handleBackspace';

export interface ICalculatorButton {
  label: string | ReactNode;
  value?: string;
  classes?: string;
  onClick: TCalculatorAction;
}

export interface ICalculatorKeypadLayout {
  cols: number;
  buttons: ICalculatorButton[];
}

export interface ICalculatorEngineState {
  display: string;
  expression: string;
  operationText: string | null;
  error: string | null;
  result: string | null;
  input: string;
}

export interface ICalculatorEngineActions {
  appendDigit: (digit?: string) => void;
  appendDecimal: () => void;
  applyOperator: (op?: string) => void;
  evaluateExpression: () => void;
  applyPercent: () => void;
  clearAll: () => void;
  backspace: () => void;
  insertToken: (token?: string) => void;
  updateInput: (newInput: string) => void;
  hydrateSharedState: (expression: string, result: string) => void;
}

export interface ICalculatorEngine {
  state: ICalculatorEngineState;
  actions: ICalculatorEngineActions;
  meta: {
    justCalculated: boolean;
  };
}

export interface ICalculatorHandlers {
  handleNumber: (value?: string) => void;
  handleDecimal: () => void;
  handleOperator: (value?: string) => void;
  handleEquals: () => void;
  handlePercent: () => void;
  handleClear: () => void;
  handleBackspace: () => void;
}

export type TCalculatorModeHandlerFactory = (
  actions: ICalculatorEngineActions,
  state?: ICalculatorEngineState,
  meta?: { justCalculated: boolean }
) => ICalculatorHandlers;

export type TConverterTab =
  | 'angle'
  | 'area'
  | 'currency'
  | 'data'
  | 'length'
  | 'speed'
  | 'temperature'
  | 'time'
  | 'volume'
  | 'weight';

export interface IUnitItem {
  name: string;
  code: string;
  mathjsName?: string; // for mathjs conversion, e.g. "degC" for temperature
}

export interface IConverterTabsScrollControls {
  tabsRef: (node: HTMLDivElement | null) => void;
  isDraggingTabs: boolean;
  handleTabsPointerDown: PointerEventHandler<HTMLDivElement>;
  handleTabsPointerMove: PointerEventHandler<HTMLDivElement>;
  handleTabsPointerEnd: PointerEventHandler<HTMLDivElement>;
}

export interface ICalculatorOwner {
  peer_id: string;
  peer_uuid: string;
  peer_name: string;
}

export interface ICalculatorSettings {
  mode: TCalculatorMode;
  converterConfigs?: {
    selectedTopUnit: string;
    selectedBottomUnit: string;
    category: TConverterTab;
    swapped: boolean;
  };
}

export interface ISharedCalculatorDetails {
  owner: ICalculatorOwner;
  settings: ICalculatorSettings;
  currentOperation?: ICalculatorOperationPayload | null;
}

export interface ISharedCalculatorData {
  isOpen: boolean;
  isSharedByMe: boolean;
  owner: ICalculatorOwner | null;
  settings: ICalculatorSettings | null;
}

export interface ISharedCalculatorState {
  expression: string;
  result: string;
  error?: string | null;
}

export interface IShareCalculatorResponse {
  calculatorDetails: ISharedCalculatorDetails | null;
}
export interface IStopSharingCalculatorResponse {
  forceStoppedByHost?: boolean;
  message?: string;
  peer_name?: string;
  from_peer_id?: string;
  from_peer_name?: string;
}
export interface IStopSharingCalculatorRequest {
  forceStoppedByHost?: boolean;
  targetPeerId?: string;
  targetRecordId?: string;
}

export interface IStopSharingCalculatorPayload {
  forceStoppedByHost?: boolean;
  targetPeerId?: string;
  targetRecordId?: string;
}

export interface IStopCalculatorParams extends IStopSharingCalculatorRequest {
  closePersonalCalculator?: boolean;
}

export interface IShareCalculatorParams {
  mode: TCalculatorMode;
  currentPeer: {
    peer_name: string;
    peer_id: string;
    peer_uuid: string;
  };
}

export interface ICalculatorOperationPayload {
  action: string;
  actionButton: string;
  expression: string;
  result: string;
  peer_info: {
    peer_id: string;
    peer_uuid: string;
    peer_name: string;
    peer_record_id: string;
  };
}

export interface ICalculatorOperationSyncPayload {
  from_peer_id: string;
  from_peer_name: string;
  from_peer_uuid: string;
  calculatorOperation: ICalculatorOperationPayload;
}

export enum ECalculatorSocketEvents {
  SHARE_CALCULATOR = 'shareCalculator',
  UPDATE_CALCULATOR = 'updateCalculator',
  STOP_SHARING_CALCULATOR = 'stopSharingCalculator',
  CALCULATOR_OPERATIONS_SYNC = 'calculatorOperationsSync',
}

export interface ICalculatorState {
  result: string | number | null | undefined;
  error?: string | null;
  expression: string | null;
}

export interface ICurrencyListResponse {
  currencies: Record<string, string>;
}

export interface IExchangeRateResponse {
  from_currency: string;
  to_currency: string;
  rate: number;
}

export interface IExchangeRateRequest {
  source: string;
  target: string;
}

export interface IConversionResult {
  convertedValue: number | string;
  displayString: string;
  rate?: number | undefined;
  result?: string | undefined;
}
